/**
 * Pine editor: write a script, run it against the current symbol, see the
 * plots on the chart and any error on the exact line that caused it.
 */
import Editor, { type Monaco } from "@monaco-editor/react";
import type { PineInput, PineRunResponse } from "@tt/shared";
import type { editor } from "monaco-editor";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAsync } from "../hooks/useApi";
import { ApiError, api } from "../lib/api";
import {
  STARTER_INDICATOR, STARTER_STRATEGY, registerCompletions, registerPine,
} from "../lib/pineLanguage";

export interface PineEditorProps {
  symbol: string;
  timeframe: string;
  bars: number;
  /** Owned by App so the Backtest panel runs the same script. */
  source: string;
  onSourceChange: (source: string) => void;
  onResult: (res: PineRunResponse | null) => void;
  /** Jumps the user into the Strategy Tester. Auto-fired when a strategy
   *  script is run, and surfaced as a button on the kind badge too, so the
   *  "I ran a strategy, where are the results?" moment never happens. */
  onOpenBacktest?: () => void;
}

export function PineEditor({
  symbol, timeframe, bars, source, onSourceChange: setSource, onResult,
  onOpenBacktest,
}: PineEditorProps) {
  const [inputs, setInputs] = useState<Record<string, unknown>>({});
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<PineRunResponse | null>(null);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [saveName, setSaveName] = useState("");
  const monacoRef = useRef<Monaco | null>(null);
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);

  // Follow the global light/dark theme. Reads from <html data-theme=…> set
  // by App.tsx and listens for changes so the Monaco palette flips in sync
  // with the rest of the chrome, no reload needed.
  const [editorTheme, setEditorTheme] = useState<"tt-dark" | "tt-light">(() =>
    typeof document !== "undefined"
      && document.documentElement.getAttribute("data-theme") === "light"
      ? "tt-light"
      : "tt-dark"
  );
  useEffect(() => {
    const mo = new MutationObserver(() => {
      const light = document.documentElement.getAttribute("data-theme") === "light";
      setEditorTheme(light ? "tt-light" : "tt-dark");
    });
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => mo.disconnect();
  }, []);

  const reference = useAsync((s) => api.pineReference(s), []);
  const scripts = useAsync((s) => api.scripts(s), []);

  // ── run ───────────────────────────────────────────────────────────────
  const run = useCallback(
    async (overrideInputs?: Record<string, unknown>) => {
      setRunning(true);
      setError(null);
      try {
        const res = await api.runPine({
          source, symbol, timeframe, bars,
          inputs: overrideInputs ?? inputs,
        });
        setResult(res);
        onResult(res);
        markErrors(res.errors);
        // A clean strategy run means they want the Strategy Tester. Only
        // on a clean compile -- if there are errors, keep them in the Pine
        // tab where the error list is right under the editor.
        if (res.declaration?.kind === "strategy" && res.errors.length === 0) {
          onOpenBacktest?.();
        }
      } catch (err) {
        setError(err as Error);
        setResult(null);
        onResult(null);
        const pine = err instanceof ApiError ? err.pineError : null;
        markErrors(pine ? [pine] : []);
      } finally {
        setRunning(false);
      }
    },
    [source, symbol, timeframe, bars, inputs, onResult],
  );

  // Re-run when the symbol or timeframe changes, so the chart never shows
  // one symbol's candles with another symbol's indicator.
  useEffect(() => {
    if (result || error) void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, timeframe, bars]);

  const markErrors = useCallback((errors: { message: string; line: number; col: number; kind?: string; hint?: string | null }[]) => {
    const monaco = monacoRef.current;
    const ed = editorRef.current;
    if (!monaco || !ed) return;
    const model = ed.getModel();
    if (!model) return;
    monaco.editor.setModelMarkers(
      model,
      "pine",
      errors.map((e) => ({
        severity: monaco.MarkerSeverity.Error,
        message: e.hint ? `${e.message}\n\nhint: ${e.hint}` : e.message,
        startLineNumber: Math.max(1, e.line),
        startColumn: Math.max(1, e.col),
        endLineNumber: Math.max(1, e.line),
        endColumn: Math.max(2, e.col + 20),
        source: e.kind ?? "pine",
      })),
    );
  }, []);

  // ── live validation while typing ──────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => {
      void api
        .validatePine(source)
        .then((v) => markErrors(v.errors))
        .catch(() => {
          // Validation failing is not worth interrupting typing over.
        });
    }, 500);
    return () => clearTimeout(t);
  }, [source, markErrors]);

  useEffect(() => {
    if (reference.data && monacoRef.current) {
      registerCompletions(monacoRef.current, reference.data);
    }
  }, [reference.data]);

  const declaredInputs = result?.inputs ?? [];
  const pineError = error instanceof ApiError ? error.pineError : null;

  const unsupported = useMemo(() => reference.data?.unsupported ?? [], [reference.data]);

  return (
    <div className="editor-wrap">
      <div className="editor-bar">
        <button className="btn primary" onClick={() => void run()} disabled={running}>
          {running ? <span className="spinner" /> : "▶"} Run on {symbol}
        </button>

        {/* Load / new, split by kind: TradingView treats strategies and
            indicators as distinct citizens with their own dialog tabs, and
            lumping them into one flat list was the first thing a user who
            compared the two surfaces noticed. */}
        <select
          className="select"
          value=""
          onChange={(e) => {
            const v = e.target.value;
            if (v === "__indicator") setSource(STARTER_INDICATOR);
            else if (v === "__strategy") setSource(STARTER_STRATEGY);
            else {
              const s = scripts.data?.scripts.find((x) => String(x.id) === v);
              if (s) { setSource(s.source); setSaveName(s.name); }
            }
          }}
        >
          <option value="">Load…</option>
          <optgroup label="New">
            <option value="__indicator">+ New indicator</option>
            <option value="__strategy">+ New strategy</option>
          </optgroup>
          {(() => {
            const all = scripts.data?.scripts ?? [];
            const strategies = all.filter((s) => s.kind === "strategy");
            const indicators = all.filter((s) => s.kind !== "strategy");
            return (
              <>
                {strategies.length > 0 && (
                  <optgroup label={`Strategies (${strategies.length})`}>
                    {strategies.map((s) => (
                      <option key={s.id} value={String(s.id)}>{s.name}</option>
                    ))}
                  </optgroup>
                )}
                {indicators.length > 0 && (
                  <optgroup label={`Indicators (${indicators.length})`}>
                    {indicators.map((s) => (
                      <option key={s.id} value={String(s.id)}>{s.name}</option>
                    ))}
                  </optgroup>
                )}
              </>
            );
          })()}
        </select>

        {/* Kind badge: visible, coloured, and when the current script is a
            strategy, labelled with the one thing the user needs next
            ("Backtest"). TV places the Strategy Tester right under the chart
            when a strategy is loaded; we live in a tab-switched layout, so
            the fastest path is a one-click prompt to that tab. */}
        {result?.declaration?.kind === "strategy" ? (
          <>
            <span className="kind-badge kind-strategy" title="This is a strategy — the Backtest tab will run it.">
              Strategy
            </span>
            {onOpenBacktest && (
              <button
                className="btn"
                onClick={() => onOpenBacktest()}
                title="Open the Strategy Tester with this script"
                style={{ marginLeft: 2 }}
              >
                Open Strategy Tester →
              </button>
            )}
          </>
        ) : result?.declaration?.kind === "indicator" ? (
          <span className="kind-badge kind-indicator" title="This is an indicator — it plots but does not place orders.">
            Indicator
          </span>
        ) : null}

        <input
          className="input"
          style={{ width: 150 }}
          placeholder="Save as…"
          value={saveName}
          onChange={(e) => setSaveName(e.target.value)}
        />
        <button
          className="btn"
          disabled={!saveName.trim()}
          onClick={async () => {
            try {
              await api.saveScript({
                name: saveName.trim(),
                source,
                kind: String(result?.declaration?.kind ?? "indicator"),
              });
              scripts.refetch();
            } catch (err) {
              setError(err as Error);
            }
          }}
        >
          Save
        </button>

        <span className="spacer" />

        {result && (
          <span className="small faint mono">
            {result.bars} bars · {result.elapsed_ms.toFixed(1)}ms ·{" "}
            {result.plots.length} plot{result.plots.length === 1 ? "" : "s"}
            {result.labels.length > 0 && ` · ${result.labels.length} labels`}
            {result.tables.length > 0 && ` · ${result.tables.length} table`}
            {result.alert_events.length > 0 &&
              ` · ${result.alert_events.length} alerts`}
          </span>
        )}
      </div>

      {declaredInputs.length > 0 && (
        <div className="editor-bar">
          <span className="small faint">Inputs</span>
          <div className="inputs-row">
            {declaredInputs.map((inp) => (
              <InputControl
                key={inp.name}
                spec={inp}
                value={inputs[inp.name]}
                onChange={(v) => {
                  const next = { ...inputs, [inp.name]: v };
                  setInputs(next);
                  void run(next);
                }}
              />
            ))}
            {Object.keys(inputs).length > 0 && (
              <button className="btn small" onClick={() => { setInputs({}); void run({}); }}>
                reset
              </button>
            )}
          </div>
        </div>
      )}

      <div className="editor-host">
        <Editor
          language="pine"
          theme={editorTheme}
          value={source}
          onChange={(v) => setSource(v ?? "")}
          beforeMount={(monaco) => { monacoRef.current = monaco; registerPine(monaco); }}
          onMount={(ed, monaco) => {
            editorRef.current = ed;
            monacoRef.current = monaco;
            if (reference.data) registerCompletions(monaco, reference.data);
            ed.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => void run());
          }}
          options={{
            fontSize: 13.5,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            minimap: { enabled: false },
            lineNumbersMinChars: 3,
            scrollBeyondLastLine: false,
            tabSize: 4,
            insertSpaces: true,
            renderWhitespace: "selection",
            automaticLayout: true,
            padding: { top: 8 },
            wordWrap: "off",
            bracketPairColorization: { enabled: true },
          }}
        />
      </div>

      {result && result.alert_events.length > 0 && (
        <div className="pine-alerts">
          <div className="small faint">
            Alerts fired ({result.alert_events.length})
            <span className="mono" style={{ marginLeft: 8 }}>
              newest first
            </span>
          </div>
          <ul className="tight mono small">
            {result.alert_events.slice().reverse().slice(0, 40).map((a, i) => (
              <li key={`${a.bar}-${i}`}>
                <span className="faint">
                  {result.times[a.bar] != null
                    ? new Date(result.times[a.bar]! * 1000).toLocaleString("en-US", {
                        timeZone: "America/New_York",
                        month: "short", day: "numeric",
                        hour: "2-digit", minute: "2-digit",
                      })
                    : `bar ${a.bar}`}
                </span>{" "}
                {a.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(pineError || error || (result && result.errors.length > 0) || (result && result.warnings.length > 0)) && (
        <div style={{ maxHeight: 150, overflowY: "auto", borderTop: "1px solid var(--border)", padding: "8px 10px" }}>
          {pineError && (
            <div className="err">
              {pineError.kind}: {pineError.message} (line {pineError.line}:{pineError.col})
              {pineError.hint ? `\nhint: ${pineError.hint}` : ""}
            </div>
          )}
          {error && !pineError && <div className="err">{error.message}</div>}
          {result?.errors.map((e, i) => (
            <div className="err" key={i}>
              {e.kind}: {e.message} (line {e.line}:{e.col})
              {e.hint ? `\nhint: ${e.hint}` : ""}
            </div>
          ))}
          {result?.warnings.map((w, i) => (
            <div className="warnbox" key={i}>{w}</div>
          ))}
          {pineError?.kind === "unsupported" && unsupported.length > 0 && (
            <div className="notebox">
              <strong>Deliberately unsupported</strong>
              <ul className="tight">
                {unsupported.map((u) => (
                  <li key={u.feature}>
                    <span className="mono">{u.feature}</span> — {u.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InputControl({
  spec, value, onChange,
}: {
  spec: PineInput;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const current = value ?? spec.default;
  if (spec.kind === "bool") {
    return (
      <label title={spec.tooltip ?? undefined}>
        <input
          type="checkbox"
          checked={Boolean(current)}
          onChange={(e) => onChange(e.target.checked)}
        />
        {spec.title}
      </label>
    );
  }
  if (spec.options && spec.options.length > 0) {
    return (
      <label title={spec.tooltip ?? undefined}>
        {spec.title}
        <select
          className="select"
          value={String(current)}
          onChange={(e) => onChange(e.target.value)}
        >
          {spec.options.map((o) => (
            <option key={String(o)} value={String(o)}>{String(o)}</option>
          ))}
        </select>
      </label>
    );
  }
  if (spec.kind === "source") {
    return (
      <label title={spec.tooltip ?? undefined}>
        {spec.title}
        <select
          className="select"
          value={String(current)}
          onChange={(e) => onChange(e.target.value)}
        >
          {["close", "open", "high", "low", "hl2", "hlc3", "ohlc4", "hlcc4", "volume"].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </label>
    );
  }
  return (
    <label title={spec.tooltip ?? undefined}>
      {spec.title}
      <input
        className="input"
        type="number"
        value={current === null || current === undefined ? "" : String(current)}
        min={spec.minval !== null && spec.minval !== undefined ? Number(spec.minval) : undefined}
        max={spec.maxval !== null && spec.maxval !== undefined ? Number(spec.maxval) : undefined}
        step={spec.step !== null && spec.step !== undefined ? Number(spec.step) : (spec.kind === "int" ? 1 : "any")}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") return;
          onChange(spec.kind === "int" ? Math.round(Number(raw)) : Number(raw));
        }}
      />
    </label>
  );
}
